import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, MessageCircle } from "lucide-react";
import api from "../lib/api";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BookDemoForm } from "../components/BookDemoForm";
import { useReveal } from "../hooks/useReveal";
import { waLink } from "../lib/wa";

export default function ModelDetailPage() {
  const { id } = useParams();
  const [model, setModel] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const rootRef = useReveal([model?.id, related.length]);

  useEffect(() => {
    setModel(null);
    setNotFound(false);
    api
      .get(`/models/${id}`)
      .then(({ data }) => {
        setModel(data);
        api
          .get("/models", { params: { category: data.category } })
          .then(({ data: list }) => setRelated(list.filter((m) => m.id !== id).slice(0, 3)));
      })
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div>
        <Navbar />
        <div className="pt-36 pb-24 text-center">
          <h1 className="h2-section text-4xl">Model not found</h1>
          <Link to="/models" className="btn-primary mt-8 inline-flex">
            <ArrowLeft size={16} /> Back to catalog
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!model) {
    return (
      <div>
        <Navbar />
        <div className="pt-36 pb-24 text-center text-[#86868B]">Loading…</div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className="bg-white" data-testid="model-detail-page">
      <Navbar />

      <section className="pt-28 md:pt-32 pb-16 bg-gradient-to-b from-[#f5f5f7] to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Link
            to="/models"
            className="inline-flex items-center gap-2 text-sm text-[#86868B] hover:text-[#FF6A13]"
            data-testid="back-to-models-link"
          >
            <ArrowLeft size={16} /> All models
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 reveal">
              <div className="overline">{model.category}</div>
              <h1 className="h1-hero text-5xl md:text-6xl mt-4">
                {model.name}
              </h1>
              <div className="mt-4 text-xs tracking-[0.3em] uppercase text-[#86868B]">
                Model Code · {model.code}
              </div>
              <p className="mt-6 text-lg text-[#86868B] max-w-xl leading-relaxed">
                {model.long_desc}
              </p>

              <a
                href="#book-section"
                className="btn-primary mt-8 inline-flex"
                data-testid="detail-book-demo-btn"
              >
                Book a demo of the {model.code}
              </a>
              <a
                href={waLink({
                  source: `model_${model.id}`,
                  message: `Hi Bhumi Equipments, I'd like to enquire about the ${model.name} (${model.code}).`,
                })}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost mt-8 ml-3 inline-flex text-[#25D366]"
                data-testid="detail-whatsapp-btn"
              >
                <MessageCircle size={16} /> WhatsApp
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/models/${model.id}/spec-sheet.pdf`}
                target="_blank"
                rel="noreferrer"
                className="btn-ghost mt-8 ml-3 inline-flex"
                data-testid="detail-download-pdf-btn"
              >
                Download Spec Sheet (PDF)
              </a>
            </div>

            <div className="lg:col-span-6 reveal">
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed] aspect-[5/4]">
                <img
                  src={model.image}
                  alt={model.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#0e0e10] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4 reveal">
            <div className="overline">Specifications</div>
            <h2 className="h2-section text-3xl md:text-5xl mt-4">
              Built to the last bolt.
            </h2>
          </div>
          <div className="lg:col-span-8 reveal">
            <dl className="divide-y divide-white/10">
              {Object.entries(model.specs).map(([k, v]) => (
                <div
                  key={k}
                  className="flex items-start justify-between py-5 gap-6"
                  data-testid={`spec-${k.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <dt className="text-sm text-white/60 uppercase tracking-widest">
                    {k}
                  </dt>
                  <dd className="font-display text-xl md:text-2xl font-semibold text-white text-right">
                    {v}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-10 flex flex-wrap gap-3">
              {[
                "Genuine parts & warranty",
                "On-site operator training",
                "Service at 8 touch points",
              ].map((x) => (
                <span
                  key={x}
                  className="inline-flex items-center gap-2 text-sm bg-white/5 border border-white/10 rounded-full px-4 py-2"
                >
                  <CheckCircle2 size={14} className="text-[#FF6A13]" /> {x}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-20 bg-[#f5f5f7]">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <h3 className="h2-section text-3xl md:text-4xl reveal">
              More in {model.category}
            </h3>
            <div className="mt-10 grid md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/models/${r.id}`}
                  className="reveal bg-white rounded-3xl overflow-hidden border border-black/5 hover:border-[#FF6A13]/30 transition-all"
                  data-testid={`related-${r.id}`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={r.image} alt={r.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <div className="font-display text-xl font-bold">{r.name}</div>
                    <div className="text-sm text-[#86868B] mt-1">{r.short_desc}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="book-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="reveal text-center">
            <div className="overline">Book a Demo</div>
            <h2 className="h2-section text-4xl md:text-5xl mt-4">
              Try the {model.code} at your site.
            </h2>
          </div>
          <div className="mt-10 reveal">
            <BookDemoForm defaultModelId={model.id} />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
